import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { format } from "date-fns";
import PropTypes from "prop-types";

// Tooltip for the "⚖️ weighted rank" row label of the product table: the
// ASIN's weekly seasonality index over the whole published cycle (52 weeks),
// from the DS team's weekly_seasonality_index table via
// /weekly-seasonality-index (store_management's data_db/seasonality.py). Each
// bar is one Sun–Sat week; the value is that week relative to the ASIN's
// annual average (1.00 = an average week), drawn up or down from the 1.00
// baseline. The index plotted is purchases_si, the same curve the Target
// Velocity Tool distributes against.

const CYCLE_DAYS = 364;
const SATURDAY = 6; // Date.getDay(): Sunday=0 .. Saturday=6

// Parse as a local date so the label doesn't drift a day in negative-offset
// timezones (new Date("2026-08-02") is UTC midnight).
const parseLocalDate = (iso) => {
  const [y, m, d] = iso.split("-").map(Number);
  return new Date(y, m - 1, d);
};
const toIsoDate = (date) => format(date, "yyyy-MM-dd");
const addDays = (date, days) => {
  const next = new Date(date.getTime());
  next.setDate(next.getDate() + days);
  return next;
};

// The Saturday ending the Sun–Sat week containing `date` — the table's week
// anchor (a Saturday maps to itself).
export const weekEndOnOrAfter = (date) => {
  const day = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  return addDays(day, (SATURDAY - day.getDay() + 7) % 7);
};

// Which bar is "this week". A run is a trailing historical cycle, so the
// current week is usually just past its end; then the same week one cycle
// (52 weeks) earlier stands in for it. -1 when neither is in the list.
export const currentWeekIndex = (weekEnds, today = new Date()) => {
  if (!Array.isArray(weekEnds) || weekEnds.length === 0) return -1;
  const thisWeek = weekEndOnOrAfter(today);
  const direct = weekEnds.indexOf(toIsoDate(thisWeek));
  if (direct !== -1) return { index: direct, lastYear: false };
  const lastCycle = weekEnds.indexOf(toIsoDate(addDays(thisWeek, -CYCLE_DAYS)));
  return lastCycle === -1 ? -1 : { index: lastCycle, lastYear: true };
};

// One x-axis label per month: the first bar whose week ends in a new month.
export const monthTicks = (weekEnds) => {
  const ticks = [];
  let lastMonth = null;
  (weekEnds || []).forEach((iso, index) => {
    const date = parseLocalDate(iso);
    const month = `${date.getFullYear()}-${date.getMonth()}`;
    if (month !== lastMonth) {
      ticks.push({ index, label: format(date, "MMM") });
      lastMonth = month;
    }
  });
  return ticks;
};

const isFiniteNumber = (value) =>
  typeof value === "number" && Number.isFinite(value);

const formatWeekRange = (weekEndIso) => {
  const end = parseLocalDate(weekEndIso);
  const start = addDays(end, -6);
  const endLabel =
    start.getMonth() === end.getMonth()
      ? format(end, "d, yyyy")
      : format(end, "MMM d, yyyy");
  return `${format(start, "MMM d")} – ${endLabel}`;
};

const CHART = {
  width: 560,
  height: 200,
  left: 36,
  right: 10,
  top: 26,
  bottom: 26,
};
// The axis always reaches the tallest week, so every bar is drawn at its true
// relative height (a flag-store July week can be 10x). Gridlines widen with
// the range so a tall axis doesn't fill up with labels.
export const buildScale = (values) => {
  const finite = values.filter(isFiniteNumber);
  const maxValue = finite.length ? Math.max(...finite) : 1;
  const yMax = Math.max(1.5, Math.ceil(maxValue * 4) / 4);
  const gridStep = yMax <= 3 ? 0.5 : yMax <= 6 ? 1 : yMax <= 12 ? 2 : 5;
  return { yMax, gridStep };
};

const COLORS = {
  above: "#60a5fa",
  below: "#f87171",
  current: "#fbbf24",
  baseline: "#e2e8f0",
  grid: "rgba(226, 232, 240, 0.18)",
  text: "#cbd5e1",
  muted: "#94a3b8",
};

export const WeightedRankSeasonalityTooltip = ({
  seasonality,
  today = new Date(),
}) => {
  const weekEnds = Array.isArray(seasonality?.week_ends)
    ? seasonality.week_ends
    : [];
  const values = Array.isArray(seasonality?.purchases_si)
    ? seasonality.purchases_si
    : [];
  const barCount = Math.min(weekEnds.length, values.length);

  const { yMax, gridStep } = buildScale(values);
  const plotWidth = CHART.width - CHART.left - CHART.right;
  const plotHeight = CHART.height - CHART.top - CHART.bottom;
  const slot = barCount > 0 ? plotWidth / barCount : plotWidth;
  const barWidth = Math.max(2, slot * 0.68);
  const y = (value) => CHART.top + ((yMax - value) / yMax) * plotHeight;

  const current = currentWeekIndex(weekEnds, today);
  const currentIndex = current === -1 ? -1 : current.index;
  // The 1.00 baseline is always drawn, even when the step skips it.
  const gridValues = gridStep > 1 ? [1] : [];
  for (let v = gridStep; v <= yMax + 1e-9; v += gridStep) gridValues.push(v);

  const runMonth =
    typeof seasonality?.run_month === "string" && seasonality.run_month
      ? format(parseLocalDate(seasonality.run_month), "MMM yyyy")
      : null;
  const coveragePct = isFiniteNumber(seasonality?.coverage)
    ? `${Math.round(seasonality.coverage * 100)}%`
    : null;
  const footer = [
    isFiniteNumber(seasonality?.n_queries)
      ? `${seasonality.n_queries} search queries`
      : null,
    coveragePct ? `coverage ${coveragePct}` : null,
    seasonality?.basis ? `basis ${seasonality.basis}` : null,
    runMonth ? `run ${runMonth}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Box sx={{ p: 1.5, width: CHART.width }}>
      <Typography
        sx={{ fontSize: "13px", fontWeight: 600, color: "#f8fafc", mb: 0.25 }}
      >
        Weekly seasonality index
      </Typography>
      <Typography sx={{ fontSize: "11px", color: COLORS.text, mb: 1 }}>
        Purchases index by week
        {barCount > 0
          ? `, ${barCount} weeks ending ${format(
              parseLocalDate(weekEnds[barCount - 1]),
              "MMM d, yyyy"
            )}`
          : ""}
        . 1.00 = an average week for this ASIN.
      </Typography>

      {barCount === 0 ? (
        <Typography sx={{ fontSize: "12px", color: COLORS.muted }}>
          No seasonality index published for this ASIN yet.
        </Typography>
      ) : (
        <svg
          width={CHART.width}
          height={CHART.height}
          viewBox={`0 0 ${CHART.width} ${CHART.height}`}
          role="img"
          aria-label="Weekly seasonality index, one bar per week"
        >
          {gridValues.map((v) => (
            <g key={v}>
              <line
                x1={CHART.left}
                x2={CHART.width - CHART.right}
                y1={y(v)}
                y2={y(v)}
                stroke={v === 1 ? COLORS.baseline : COLORS.grid}
                strokeWidth={v === 1 ? 1 : 0.5}
              />
              <text
                x={CHART.left - 6}
                y={y(v) + 3}
                fontSize="9"
                textAnchor="end"
                fill={COLORS.muted}
              >
                {v.toFixed(1)}
              </text>
            </g>
          ))}

          {weekEnds.slice(0, barCount).map((weekEnd, index) => {
            const value = values[index];
            if (!isFiniteNumber(value)) return null;
            const x = CHART.left + index * slot + (slot - barWidth) / 2;
            const top = y(Math.max(value, 1));
            const bottom = y(Math.min(value, 1));
            const isCurrent = index === currentIndex;
            return (
              <rect
                key={weekEnd}
                className="si-bar"
                x={x}
                y={top}
                width={barWidth}
                height={Math.max(bottom - top, 1)}
                fill={
                  isCurrent
                    ? COLORS.current
                    : value >= 1
                    ? COLORS.above
                    : COLORS.below
                }
                opacity={isCurrent ? 1 : 0.85}
              >
                <title>{`${formatWeekRange(weekEnd)}: ${value.toFixed(
                  2
                )}`}</title>
              </rect>
            );
          })}

          {currentIndex !== -1 && (
            <g className="si-now">
              {/* "Now" marker: a dashed line through the current week's bar,
                  across the whole plot so it reads at a glance. */}
              <line
                x1={CHART.left + currentIndex * slot + slot / 2}
                x2={CHART.left + currentIndex * slot + slot / 2}
                y1={CHART.top - 4}
                y2={CHART.top + plotHeight}
                stroke={COLORS.current}
                strokeWidth={1}
                strokeDasharray="3 2"
              />
              <text
                x={Math.min(
                  Math.max(
                    CHART.left + currentIndex * slot + slot / 2,
                    CHART.left + 60
                  ),
                  CHART.width - CHART.right - 60
                )}
                y={CHART.top - 10}
                fontSize="10"
                fontWeight="600"
                textAnchor="middle"
                fill={COLORS.current}
              >
                {current.lastYear ? "Now (same week last year)" : "Now"}
              </text>
            </g>
          )}

          {monthTicks(weekEnds.slice(0, barCount)).map(({ index, label }) => (
            <g key={`${label}-${index}`}>
              <line
                x1={CHART.left + index * slot}
                x2={CHART.left + index * slot}
                y1={CHART.top + plotHeight}
                y2={CHART.top + plotHeight + 4}
                stroke={COLORS.muted}
                strokeWidth={0.5}
              />
              <text
                x={CHART.left + index * slot + 2}
                y={CHART.top + plotHeight + 15}
                fontSize="9"
                fill={COLORS.muted}
              >
                {label}
              </text>
            </g>
          ))}
        </svg>
      )}

      {footer && (
        <Typography sx={{ fontSize: "11px", color: COLORS.muted, mt: 0.5 }}>
          {footer}
        </Typography>
      )}
    </Box>
  );
};

WeightedRankSeasonalityTooltip.propTypes = {
  seasonality: PropTypes.shape({
    run_month: PropTypes.string,
    week_ends: PropTypes.arrayOf(PropTypes.string),
    purchases_si: PropTypes.arrayOf(PropTypes.number),
    n_queries: PropTypes.number,
    coverage: PropTypes.number,
    basis: PropTypes.string,
  }),
  today: PropTypes.instanceOf(Date),
};
